"use client";

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useLanguage } from "../../../../context/LanguageContext";

export default function Tutorials() {
  const [frame1Loaded, setFrame1Loaded] = useState(false);
  const [frame2Loaded, setFrame2Loaded] = useState(false);
  const { language } = useLanguage();

  const iframeSources = {
    en: [
      "https://scribehow.com/embed/How_To_Create_A_New_Form_Step_By_Step__DrgPYxp_QQCO8_eKn7D_Ug?as=scrollable",
      "https://scribehow.com/embed/How_To_Create_A_New_Form_Step_By_Step__DrgPYxp_QQCO8_eKn7D_Ug?as=scrollable",
    ],
    de: [
      "https://scribehow.com/embed/Erstelle_dein_Formular__Ub8OGX8HTEWUYC75HDCAGw?as=scrollable",
      "https://scribehow.com/embed/QR_Code_erstellen_und_downloaden__y89WynnLQIeP0GkowNC-8g?as=scrollable",
    ],
  };

  const [formSrc, qrCodeSrc] = iframeSources[language] ?? iframeSources.en;

  return (
    <div className="container mx-auto py-8">
      <div className="w-full flex flex-col gap-4 p-4 mb-8">
        <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold">
          1.{" "}
          {language === "de"
            ? "Schritt: Erstelle dein Feedback-Formular"
            : "Step: Create your Feedback Form"}
        </h2>
        <div className="relative w-full h-[640px] bg-gray-50 rounded-lg border">
          {!frame1Loaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-12 w-12 animate-spin text-gray-500" />
              <span className="ml-2 text-gray-500">
                {language === "de" ? "Lade Tutorial..." : "Loading tutorial..."}
              </span>
            </div>
          )}
          <iframe
            src={formSrc}
            width="100%"
            height="100%"
            className={`rounded-lg transition-opacity duration-300 ${
              frame1Loaded ? "opacity-100" : "opacity-0"
            }`}
            allowFullScreen
            frameBorder="0"
            onLoad={() => setFrame1Loaded(true)}
          ></iframe>
        </div>
      </div>

      <div className="w-full flex flex-col gap-4 p-4">
        <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold">
          2.{" "}
          {language === "de"
            ? "Schritt: QR-Code erstellen und verbinden"
            : "Step: Create and connect your QR-Code"}
        </h2>
        <div className="relative w-full h-[640px] bg-gray-50 rounded-lg border">
          {!frame2Loaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-12 w-12 animate-spin text-gray-500" />
              <span className="ml-2 text-gray-500">
                {language === "de" ? "Lade Tutorial..." : "Loading tutorial..."}
              </span>
            </div>
          )}
          <iframe
            src={qrCodeSrc}
            width="100%"
            height="100%"
            className={`rounded-lg transition-opacity duration-300 ${
              frame2Loaded ? "opacity-100" : "opacity-0"
            }`}
            allowFullScreen
            frameBorder="0"
            onLoad={() => setFrame2Loaded(true)}
          ></iframe>
        </div>
      </div>

      {/* Gewinnspiel Tutorial Section */}
      <div className="w-full flex flex-col gap-4 p-4 mt-12">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl font-bold shadow-lg">
            3
          </div>
          <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
            {language === "de"
              ? "Gewinnspiel-Tool nutzen"
              : "Using the Giveaway Tool"}
          </h2>
        </div>

        <div className="bg-gradient-to-br from-orange-50 via-white to-orange-50 rounded-xl p-6 md:p-8 border-2 border-orange-200 shadow-lg">
          {/* Intro */}
          <div className="mb-8 pb-6 border-b-2 border-orange-100">
            <div className="flex items-start gap-3">
              <svg
                className="w-8 h-8 text-orange-500 flex-shrink-0 mt-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                />
              </svg>
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  {language === "de" ? "Überblick" : "Overview"}
                </h3>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {language === "de"
                    ? "Mit dem Gewinnspiel-Tool können Sie Ihre Kunden für das Ausfüllen von Feedback-Formularen belohnen. Jede ausgefüllte Teilnahme wird automatisch als Los gespeichert und Sie können später die Gewinner fair auslosen."
                    : "With the Giveaway Tool, you can reward your customers for completing feedback forms. Each completed submission is automatically saved as an entry, and you can later draw winners fairly."}
                </p>
              </div>
            </div>
          </div>

          {/* Step-by-Step Guide */}
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-orange-500">
              <div className="flex items-start gap-4">
                <div className="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800 mb-2">
                    {language === "de"
                      ? "Gewinnspiel erstellen"
                      : "Create Giveaway"}
                  </h4>
                  <p className="text-gray-700 mb-3">
                    {language === "de"
                      ? "Navigieren Sie zum Gewinnspiel-Manager und erstellen Sie ein neues Gewinnspiel. Geben Sie einen aussagekräftigen Titel, eine Beschreibung und die Anzahl der Gewinner an."
                      : "Navigate to the Giveaway Manager and create a new giveaway. Enter a meaningful title, description, and the number of winners."}
                  </p>
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <p className="text-sm text-gray-600 flex items-start gap-2">
                      <svg
                        className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>
                        {language === "de"
                          ? "Wichtig: Legen Sie ein realistisches Enddatum fest, bis wann Kunden teilnehmen können."
                          : "Important: Set a realistic end date by which customers can participate."}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-orange-500">
              <div className="flex items-start gap-4">
                <div className="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800 mb-2">
                    {language === "de"
                      ? "Gewinnspiel mit Formular verknüpfen"
                      : "Link Giveaway to Form"}
                  </h4>
                  <p className="text-gray-700 mb-3">
                    {language === "de"
                      ? "Aktivieren Sie das Gewinnspiel für Ihr gewünschtes Feedback-Formular. Ab diesem Moment wird jede Formular-Einsendung automatisch als Los für das Gewinnspiel gezählt."
                      : "Activate the giveaway for your desired feedback form. From this moment on, every form submission will automatically count as an entry for the giveaway."}
                  </p>
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <p className="text-sm text-gray-600 flex items-start gap-2">
                      <svg
                        className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span>
                        <strong>
                          {language === "de" ? "Wichtig:" : "Important:"}
                        </strong>{" "}
                        {language === "de"
                          ? "Pro Formular kann nur EIN aktives Gewinnspiel gleichzeitig laufen."
                          : "Only ONE active giveaway can run per form at a time."}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-orange-500">
              <div className="flex items-start gap-4">
                <div className="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800 mb-2">
                    {language === "de"
                      ? "Teilnehmer sammeln"
                      : "Collect Participants"}
                  </h4>
                  <p className="text-gray-700 mb-3">
                    {language === "de"
                      ? "Während der Laufzeit sammelt das System automatisch alle Teilnehmer. Sie können jederzeit die aktuelle Anzahl der Lose und Teilnehmer im Gewinnspiel-Manager einsehen."
                      : "During the runtime, the system automatically collects all participants. You can view the current number of entries and participants in the Giveaway Manager at any time."}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <svg
                      className="w-5 h-5 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      {language === "de"
                        ? "Die Teilnahme ist für Kunden automatisch und erfordert keine zusätzlichen Schritte."
                        : "Participation is automatic for customers and requires no additional steps."}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-orange-500">
              <div className="flex items-start gap-4">
                <div className="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">
                  4
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800 mb-2">
                    {language === "de"
                      ? "Gewinnspiel beenden & Gewinner auslosen"
                      : "End Giveaway & Draw Winners"}
                  </h4>
                  <p className="text-gray-700 mb-3">
                    {language === "de"
                      ? "Nach Ablauf des Gewinnspiels beenden Sie es im Gewinnspiel-Manager. Klicken Sie auf 'Gewinner auslosen', um das System automatisch und fair die Gewinner ziehen zu lassen."
                      : "After the giveaway ends, close it in the Giveaway Manager. Click 'Draw Winners' to have the system automatically and fairly select the winners."}
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <div className="flex items-center gap-2 text-sm text-green-700 font-medium mb-1">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>
                          {language === "de"
                            ? "Faire Auswahl"
                            : "Fair Selection"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {language === "de"
                          ? "Zufällige und transparente Ziehung"
                          : "Random and transparent drawing"}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <div className="flex items-center gap-2 text-sm text-purple-700 font-medium mb-1">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                        <span>
                          {language === "de" ? "Automatisch" : "Automatic"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {language === "de"
                          ? "Kein manueller Aufwand nötig"
                          : "No manual effort required"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-orange-500">
              <div className="flex items-start gap-4">
                <div className="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">
                  5
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800 mb-2">
                    {language === "de"
                      ? "Gewinner benachrichtigen & Preise versenden"
                      : "Notify Winners & Send Prizes"}
                  </h4>
                  <p className="text-gray-700 mb-3">
                    {language === "de"
                      ? "Im Winner-Tab sehen Sie alle ausgelosten Gewinner mit ihren Kontaktdaten. Die Benachrichtigung der Gewinner und das Versenden der Preise erfolgt durch Sie."
                      : "In the Winner tab, you'll see all drawn winners with their contact information. Notifying winners and sending prizes is done by you."}
                  </p>
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <p className="text-sm text-gray-700 flex items-start gap-2">
                      <svg
                        className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span>
                        <strong>
                          {language === "de" ? "Hinweis:" : "Note:"}
                        </strong>{" "}
                        {language === "de"
                          ? "Das Tool unterstützt Sie bei der Organisation, aber der Versand und die Kommunikation mit den Gewinnern liegt in Ihrer Verantwortung. Sie können den Status der Gewinner im System verfolgen (Ausstehend, Kontaktiert, Preis versendet)."
                          : "The tool assists you with organization, but shipping and communication with winners is your responsibility. You can track the status of winners in the system (Pending, Contacted, Prize Sent)."}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info Box */}
          <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
            <div className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <div>
                <h4 className="font-bold text-gray-800 text-lg mb-2">
                  {language === "de" ? "Profi-Tipps" : "Pro Tips"}
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold">•</span>
                    <span>
                      {language === "de"
                        ? "Bewerben Sie Ihr Gewinnspiel aktiv bei Ihren Kunden, um mehr Teilnehmer zu gewinnen"
                        : "Actively promote your giveaway to customers to increase participation"}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold">•</span>
                    <span>
                      {language === "de"
                        ? "Wählen Sie attraktive Preise, die zu Ihrer Zielgruppe passen"
                        : "Choose attractive prizes that match your target audience"}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold">•</span>
                    <span>
                      {language === "de"
                        ? "Nutzen Sie die Statusverwaltung im Winner-Tab, um den Überblick über alle Gewinner zu behalten"
                        : "Use the status management in the Winner tab to keep track of all winners"}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold">•</span>
                    <span>
                      {language === "de"
                        ? "Ein Gewinnspiel steigert die Motivation Ihrer Kunden, wertvolles Feedback zu geben"
                        : "A giveaway increases your customers' motivation to provide valuable feedback"}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
